import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { ref as storageRef, uploadBytes } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { TrashIcon } from "@heroicons/react/24/outline";
import { storage, functions } from "../../firebase/config";
import { useAuth } from "../../contexts/AuthContext";
import {
  detectExcludedDocument,
  normaliseNzMobile
} from "../../lib/compliance";
import { listContacts, upsertContact } from "../../lib/contacts";
import FieldPlacer from "../../components/FieldPlacer";

const emptySigner = () => ({
  id: crypto.randomUUID(),
  name: "",
  email: "",
  phone: ""
});

export default function NewDocument() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [mode, setMode] = useState("parallel");
  const [verificationMode, setVerificationMode] = useState("sms_email");
  const [signers, setSigners] = useState([emptySigner()]);
  const [fields, setFields] = useState([]);
  const [exclusion, setExclusion] = useState(null);
  const [override, setOverride] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    if (!user) return;
    listContacts(user.uid)
      .then(setContacts)
      .catch(() => setContacts([]));
  }, [user]);

  const applyContact = (signerId, contactId) => {
    const c = contacts.find((x) => x.id === contactId);
    if (!c) return;
    updateSigner(signerId, {
      name: c.name,
      email: c.email,
      phone: c.phoneE164
    });
  };

  const fileUrl = useMemo(
    () => (file ? URL.createObjectURL(file) : null),
    [file]
  );

  const onDrop = (accepted) => {
    const f = accepted[0];
    if (!f) return;
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.pdf$/i, ""));
    setExclusion(detectExcludedDocument(f.name));
    setOverride(false);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/pdf": [".pdf"] },
    maxFiles: 1
  });

  const updateSigner = (id, patch) =>
    setSigners((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...patch } : s))
    );

  const removeSigner = (id) => {
    setSigners((prev) => prev.filter((s) => s.id !== id));
    setFields((prev) => prev.filter((f) => f.signerId !== id));
  };

  const validate = () => {
    if (!file) return "Please upload a PDF first.";
    if (!title.trim()) return "Add a document title.";
    if (exclusion && !override)
      return "This document type needs an acknowledgement before sending.";
    if (signers.length === 0) return "Add at least one signer.";
    for (const s of signers) {
      if (!s.name.trim()) return "Each signer needs a name.";
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s.email))
        return `Invalid email for ${s.name || "signer"}.`;
      if (verificationMode === "sms_email" && !normaliseNzMobile(s.phone))
        return `Invalid NZ mobile for ${s.name || "signer"}. Use +64 2x xxx xxxx.`;
    }
    const missingSig = signers.find(
      (s) =>
        !fields.some(
          (f) => f.signerId === s.id && (f.type ?? "signature") === "signature"
        )
    );
    if (missingSig)
      return `Place a signature box on the PDF for ${missingSig.name}.`;
    return null;
  };

  const onSend = async () => {
    setError("");
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setSubmitting(true);
    try {
      const createSigningRequest = httpsCallable(
        functions,
        "createSigningRequest"
      );
      const payload = {
        title: title.trim(),
        mode,
        verificationMode,
        signers: signers.map((s) => ({
          name: s.name.trim(),
          email: s.email.trim().toLowerCase(),
          phoneE164:
            verificationMode === "sms_email"
              ? normaliseNzMobile(s.phone)
              : s.phone
              ? normaliseNzMobile(s.phone)
              : null,
          fields: fields
            .filter((f) => f.signerId === s.id)
            .map(({ signerId: _sid, ...rest }) => rest)
        })),
        excludedOverrideAck: override
          ? `${user.email} acknowledged CCLA Schedule 5 exclusion for "${title}" at ${new Date().toISOString()}`
          : null
      };
      const { data } = await createSigningRequest(payload);
      const { docId, uploadToken } = data;
      const path = `documents/${docId}/original.pdf`;
      await uploadBytes(storageRef(storage, path), file, {
        customMetadata: { uploadToken }
      });
      const markReady = httpsCallable(functions, "markDocumentReady");
      await markReady({ docId, uploadToken });

      // Persist every signer for quick re-use on future documents.
      await Promise.all(
        payload.signers.map((s) =>
          upsertContact(user.uid, s).catch(() => null)
        )
      );

      navigate(`/admin/documents/${docId}`);
    } catch (err) {
      setError(err.message || "Failed to create signing request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl p-4">
      <h1 className="mb-4 text-2xl font-semibold">New signing request</h1>

      <section className="card mb-4 space-y-3">
        <label className="block">
          <span className="text-sm text-slate-700">Document title</span>
          <input
            className="input mt-1"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setExclusion(detectExcludedDocument(e.target.value));
              setOverride(false);
            }}
            placeholder="e.g. Annual Report 2026"
          />
        </label>

        <div
          {...getRootProps()}
          className={
            "cursor-pointer rounded-lg border-2 border-dashed p-6 text-center " +
            (isDragActive
              ? "border-brand-500 bg-brand-50"
              : "border-slate-300 bg-slate-50")
          }
        >
          <input {...getInputProps()} />
          {file ? (
            <div>
              <div className="font-medium">{file.name}</div>
              <div className="text-sm text-slate-500">
                {(file.size / 1024).toFixed(0)} KB — tap to replace
              </div>
            </div>
          ) : (
            <div className="text-slate-500">
              Drop a PDF here or tap to choose
            </div>
          )}
        </div>

        {exclusion && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <p>{exclusion.reason}</p>
            <label className="mt-2 flex items-center gap-2">
              <input
                type="checkbox"
                checked={override}
                onChange={(e) => setOverride(e.target.checked)}
              />
              I confirm this document is not excluded from electronic signature.
            </label>
          </div>
        )}

        <fieldset>
          <legend className="text-sm text-slate-700">Signing order</legend>
          <div className="mt-1 flex gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={mode === "parallel"}
                onChange={() => setMode("parallel")}
              />
              Parallel (anyone can sign first)
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={mode === "sequential"}
                onChange={() => setMode("sequential")}
              />
              Sequential (one after another)
            </label>
          </div>
        </fieldset>

        <fieldset>
          <legend className="text-sm text-slate-700">Identity check</legend>
          <div className="mt-1 space-y-1 text-sm">
            <label className="flex items-start gap-2">
              <input
                type="radio"
                className="mt-1"
                checked={verificationMode === "sms_email"}
                onChange={() => setVerificationMode("sms_email")}
              />
              <span>
                <strong>SMS + email</strong> — recommended for company documents
                going to the Companies Office. Two-factor.
              </span>
            </label>
            <label className="flex items-start gap-2">
              <input
                type="radio"
                className="mt-1"
                checked={verificationMode === "email_only"}
                onChange={() => setVerificationMode("email_only")}
              />
              <span>
                <strong>Email only</strong> — signers can sign from a laptop
                without a phone. Single-factor; fine for low-stakes whanau
                documents.
              </span>
            </label>
          </div>
        </fieldset>
      </section>

      <section className="card mb-4">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-semibold">Signers</h2>
          <button
            className="btn-secondary text-sm"
            onClick={() => setSigners((p) => [...p, emptySigner()])}
          >
            + Add signer
          </button>
        </div>
        <ul className="space-y-3">
          {signers.map((s, idx) => (
            <li
              key={s.id}
              className="rounded-lg border border-slate-200 bg-slate-50 p-3"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-slate-500">Signer {idx + 1}</span>
                <div className="flex items-center gap-2">
                  {contacts.length > 0 && (
                    <select
                      className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
                      value=""
                      onChange={(e) => {
                        applyContact(s.id, e.target.value);
                        e.target.value = "";
                      }}
                    >
                      <option value="">Pick saved signer…</option>
                      {contacts.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} — {c.email}
                        </option>
                      ))}
                    </select>
                  )}
                  {signers.length > 1 && (
                    <button
                      onClick={() => removeSigner(s.id)}
                      className="text-slate-500 hover:text-red-600"
                      aria-label="Remove signer"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-2 grid gap-2 sm:grid-cols-3">
                <input
                  className="input"
                  placeholder="Full legal name"
                  value={s.name}
                  onChange={(e) => updateSigner(s.id, { name: e.target.value })}
                />
                <input
                  className="input"
                  type="email"
                  placeholder="Email"
                  value={s.email}
                  onChange={(e) => updateSigner(s.id, { email: e.target.value })}
                />
                <input
                  className="input"
                  type="tel"
                  placeholder={
                    verificationMode === "email_only"
                      ? "+64 21 … (optional)"
                      : "+64 21 123 4567"
                  }
                  value={s.phone}
                  onChange={(e) => updateSigner(s.id, { phone: e.target.value })}
                />
              </div>
            </li>
          ))}
        </ul>
      </section>

      {file && (
        <section className="card mb-4">
          <h2 className="mb-2 font-semibold">Signature fields</h2>
          <FieldPlacer
            file={fileUrl}
            signers={signers}
            fields={fields}
            onAddField={(f) => setFields((prev) => [...prev, f])}
            onRemoveField={(idx) =>
              setFields((prev) => prev.filter((_, i) => i !== idx))
            }
            onResizeField={(idx, patch) =>
              setFields((prev) =>
                prev.map((f, i) => (i === idx ? { ...f, ...patch } : f))
              )
            }
            onMoveField={(idx, patch) =>
              setFields((prev) =>
                prev.map((f, i) => (i === idx ? { ...f, ...patch } : f))
              )
            }
          />
        </section>
      )}

      {error && (
        <p className="mb-2 rounded bg-red-50 p-2 text-sm text-red-700">
          {error}
        </p>
      )}

      <div className="sticky bottom-4 flex justify-end">
        <button
          className="btn-primary shadow-lg"
          onClick={onSend}
          disabled={submitting}
        >
          {submitting ? "Sending…" : "Send for signing"}
        </button>
      </div>
    </div>
  );
}
